import { t } from "@lingui/macro";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { type SubmitHandler, useFieldArray, useForm } from "react-hook-form";

export type ExperienceEntry = {
  title: string;
  company: string;
  description: string;
};

export type ProfileFormValues = {
  headline: string;
  about: string;
  experiences: ExperienceEntry[];
  targetRole?: string;
};

type Props = {
  defaultValues?: ProfileFormValues;
  onSubmit: SubmitHandler<ProfileFormValues>;
  isLoading: boolean;
};

const validate = (values: ProfileFormValues): Partial<Record<string, string>> => {
  const errors: Partial<Record<string, string>> = {};
  if (!values.headline.trim()) errors.headline = t`Headline is required`;
  else if (values.headline.length > 220) errors.headline = t`Max 220 characters`;
  if (!values.about.trim()) errors.about = t`About section is required`;
  else if (values.about.length > 2600) errors.about = t`Max 2600 characters`;
  if (values.experiences.length === 0)
    errors.experiences = t`At least one experience entry is required`;
  if (values.targetRole && values.targetRole.length > 500)
    errors.targetRole = t`Max 500 characters`;
  return errors;
};

export const ProfileInputForm = ({ defaultValues, onSubmit, isLoading }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    defaultValues: defaultValues ?? {
      headline: "",
      about: "",
      experiences: [{ title: "", company: "", description: "" }],
      targetRole: "",
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "experiences" });

  const submitWithValidation: SubmitHandler<ProfileFormValues> = (values) => {
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) return;
    onSubmit(values);
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(submitWithValidation)}>
      {/* Headline */}
      <div className="space-y-2">
        <Label htmlFor="headline">
          {t`LinkedIn Headline`} <span className="text-destructive">*</span>
        </Label>
        <Input
          id="headline"
          placeholder={t`e.g. Senior Product Manager | SaaS & B2B Growth`}
          maxLength={220}
          {...register("headline", {
            required: t`Headline is required`,
            maxLength: { value: 220, message: t`Max 220 characters` },
          })}
        />
        {errors.headline && <p className="text-destructive text-sm">{errors.headline.message}</p>}
      </div>

      {/* About */}
      <div className="space-y-2">
        <Label htmlFor="about">
          {t`About Section`} <span className="text-destructive">*</span>
        </Label>
        <textarea
          id="about"
          rows={6}
          maxLength={2600}
          placeholder={t`Paste your LinkedIn About section here...`}
          className={cn(
            "w-full resize-y rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-primary/40",
          )}
          {...register("about", {
            required: t`About section is required`,
            maxLength: { value: 2600, message: t`Max 2600 characters` },
          })}
        />
        {errors.about && <p className="text-destructive text-sm">{errors.about.message}</p>}
      </div>

      {/* Experiences */}
      <div className="space-y-3">
        <Label>
          {t`Experience`} <span className="text-destructive">*</span>
        </Label>
        {fields.map((field, index) => (
          <div key={field.id} className="relative space-y-3 rounded-xl border p-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {t`Position`} {index + 1}
              </span>
              {fields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="ml-auto h-7 w-7"
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <TrashIcon className="text-destructive h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor={`experiences.${index}.title`}>{t`Job Title`}</Label>
                <Input
                  id={`experiences.${index}.title`}
                  maxLength={100}
                  placeholder={t`e.g. Senior Product Manager`}
                  {...register(`experiences.${index}.title`, {
                    required: t`Job title is required`,
                  })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`experiences.${index}.company`}>{t`Company`}</Label>
                <Input
                  id={`experiences.${index}.company`}
                  maxLength={100}
                  placeholder={t`e.g. Acme Corp`}
                  {...register(`experiences.${index}.company`, {
                    required: t`Company is required`,
                  })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`experiences.${index}.description`}>{t`Description`}</Label>
              <textarea
                id={`experiences.${index}.description`}
                rows={3}
                maxLength={2000}
                placeholder={t`Describe your responsibilities and achievements...`}
                className={cn(
                  "w-full resize-y rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-primary/40",
                )}
                {...register(`experiences.${index}.description`, {
                  required: t`Description is required`,
                })}
              />
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11 sm:min-h-0"
          onClick={() => {
            append({ title: "", company: "", description: "" });
          }}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          {t`Add Experience`}
        </Button>
      </div>

      {/* Target Role (optional) */}
      <div className="space-y-2">
        <Label htmlFor="targetRole">
          {t`Target Role`} <span className="text-xs text-muted-foreground">({t`optional`})</span>
        </Label>
        <textarea
          id="targetRole"
          rows={3}
          maxLength={500}
          placeholder={t`e.g. VP of Product at a Series B startup — or paste a job description`}
          className={cn(
            "w-full resize-y rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none transition focus-visible:ring-2 focus-visible:ring-primary/40 sm:h-[66px]",
          )}
          {...register("targetRole", { maxLength: { value: 500, message: t`Max 500 characters` } })}
        />
        {errors.targetRole && (
          <p className="text-destructive text-sm">{errors.targetRole.message}</p>
        )}
      </div>

      <Button type="submit" disabled={isLoading} className="w-full sm:w-auto">
        {isLoading ? t`Analyzing...` : t`Get My LinkedIn Score`}
      </Button>
    </form>
  );
};
